import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { useNotificationStore } from '@/store/notification-store'
import { opportunitiesService } from '@/services/opportunities-service'
import { notificationsService } from '@/services/notifications-service'
import { usersService } from '@/services/users-service'
import { AcceptOpportunityModal } from '@/components/opportunities/accept-opportunity-modal'
import { ConvertToRfpModal } from '@/components/opportunities/convert-to-rfp-modal'
import type { OpportunityDetail, OpportunityNote, User } from '@/types'

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
}

/**
 * Renders SAM.gov solicitation text with proper structure.
 * The stripped text uses \t\t for bullet items, \t for sub-labels,
 * and lines starting with a digit+period for section headers.
 */
function ScopeOfWorkRenderer({ text }: { text: string }) {
  // Strip markdown bold/italic markers that LLMs sometimes include
  const cleaned = text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1').replace(/__([^_]+)__/g, '$1')
  const lines = cleaned.split('\n')
  const elements: React.ReactNode[] = []
  let listItems: string[] = []
  let key = 0

  const flushList = () => {
    if (listItems.length === 0) return
    elements.push(
      <ul className="opp-scope-list" key={key++}>
        {listItems.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    )
    listItems = []
  }

  for (const rawLine of lines) {
    const trimmed = rawLine.trim()
    if (!trimmed) { flushList(); continue }

    // Double-tab (or 4+ leading spaces) → bullet list item
    if (rawLine.startsWith('\t\t') || /^ {4}/.test(rawLine)) {
      listItems.push(trimmed)
      continue
    }

    flushList()

    // Numbered section header: "1. James F. Battin..." or " 1. ..."
    if (/^\d+\./.test(trimmed)) {
      elements.push(<p className="opp-scope-section-header" key={key++}>{trimmed}</p>)
      continue
    }

    // Single-tab → sub-label (e.g. "Primary Work : ...", "Key Tasks :")
    if (rawLine.startsWith('\t') || /^ {2}/.test(rawLine)) {
      // Label ending with colon = a heading-like line; otherwise plain label
      if (trimmed.endsWith(':')) {
        elements.push(<p className="opp-scope-sublabel opp-scope-sublabel--head" key={key++}>{trimmed}</p>)
      } else {
        elements.push(<p className="opp-scope-sublabel" key={key++}>{trimmed}</p>)
      }
      continue
    }

    // Top-level text = paragraph
    elements.push(<p className="opp-scope-para" key={key++}>{trimmed}</p>)
  }

  flushList()
  return <div className="opp-scope-rendered">{elements}</div>
}

// Plain-English labels for common paving-related NAICS codes
const NAICS_LABELS: Record<string, string> = {
  '237310': 'Highway, Street, and Bridge Construction',
  '237990': 'Other Heavy and Civil Engineering Construction',
  '238110': 'Poured Concrete Foundation and Structure Contractors',
  '238140': 'Masonry Contractors',
  '238990': 'All Other Specialty Trade Contractors',
  '236220': 'Commercial and Institutional Building Construction',
  '237120': 'Oil and Gas Pipeline and Related Structures Construction',
  '237130': 'Power and Communication Line Construction',
  '237210': 'Land Subdivision',
  '237290': 'Other Heavy and Civil Engineering Construction',
}

function naicsLabel(code?: string | null): string | null {
  if (!code) return null
  return NAICS_LABELS[code] || null
}

// function _formatDateTime(_dateStr?: string | null): string {
//   if (!_dateStr) return ''
//   const d = new Date(_dateStr)
//   return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
//     ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
// }

/* Inline SVG icons */
const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
)

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

// function _getSourceLabel(_source: string) {
//   switch (_source) {
//     case 'pdf_upload': return 'PDF Upload'
//     case 'url': return 'URL'
//     case 'manual': return 'Manual Entry'
//     case 'auto_feed': return 'Auto Feed'
//     default: return 'Unknown'
//   }
// }

/* ── Satellite Map Card ── */
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || ''

const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

function SatelliteMapCard({ address, lat, lng }: { address: string; lat?: number | null; lng?: number | null }) {
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    lat != null && lng != null ? { lat, lng } : null
  )
  const [loading, setLoading] = useState(!coords)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (coords || !address || !MAPBOX_TOKEN) return
    setLoading(true)
    fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&limit=1&types=address,place`)
      .then(r => r.json())
      .then(data => {
        if (data.features?.[0]?.center) {
          const [lngVal, latVal] = data.features[0].center
          setCoords({ lat: latVal, lng: lngVal })
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [address, coords])

  if (!MAPBOX_TOKEN || error) return null

  const imgUrl = coords
    ? `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/pin-s+0074F4(${coords.lng},${coords.lat})/${coords.lng},${coords.lat},17,0/600x260@2x?access_token=${MAPBOX_TOKEN}`
    : ''

  return (
    <div className="opp-sat-map-card">
      {loading ? (
        <div className="opp-sat-map-loading">
          <div className="rfp-spinner" />
        </div>
      ) : coords ? (
        <>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="opp-sat-map-link"
          >
            <img
              src={imgUrl}
              alt="Satellite view of project location"
              className="opp-sat-map-img"
            />
          </a>
          <div className="opp-sat-map-address-row">
            <MapPinIcon />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="opp-sat-map-address-text"
            >
              {address}
            </a>
          </div>
        </>
      ) : null}
    </div>
  )
}

/* ── AI Insights Card (Redesigned — confidence meter + practical insights) ── */
interface AiInsightsCardProps {
  overallConfidence?: number | null
  costEstimate?: import('@/types').OpportunityCostEstimate | null
  siteAnalysis?: import('@/types').OpportunitySiteAnalysis | null
  aiSummary?: string | null
  pendingAnalysis?: boolean
}

function AiInsightsCard({ overallConfidence, costEstimate, siteAnalysis, aiSummary, pendingAnalysis }: AiInsightsCardProps) {
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [animated, setAnimated] = useState(false)
  const [countUp, setCountUp] = useState(0)
  // Track whether we've ever been in pending state so we can animate the reveal
  const [wasPending, setWasPending] = useState(!!pendingAnalysis)
  const [revealReady, setRevealReady] = useState(false)

  const isLoading = !!pendingAnalysis && !costEstimate
  const hasResults = overallConfidence != null || costEstimate != null

  // Track when we transition from loading → results
  useEffect(() => {
    if (pendingAnalysis) setWasPending(true)
  }, [pendingAnalysis])

  useEffect(() => {
    if (wasPending && hasResults && !isLoading) {
      // Small delay to let the skeleton fade out before animating in
      const t = setTimeout(() => setRevealReady(true), 100)
      return () => clearTimeout(t)
    }
    if (!wasPending && hasResults) {
      setRevealReady(true)
    }
  }, [wasPending, hasResults, isLoading])

  const pct = overallConfidence != null ? Math.round(overallConfidence * 100) : 0
  const circumference = 2 * Math.PI * 52
  const dashLen = animated && overallConfidence ? overallConfidence * circumference : 0

  useEffect(() => {
    if (!revealReady) return
    const t = setTimeout(() => setAnimated(true), 200)
    return () => clearTimeout(t)
  }, [revealReady])

  // Count-up animation
  useEffect(() => {
    if (!animated || !pct) return
    let frame: number
    const duration = 1200
    const start = performance.now()
    function tick(now: number) {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCountUp(Math.round(eased * pct))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [animated, pct])

  const hasScore = overallConfidence != null
  const ce = costEstimate

  // Confidence tier label
  const tierLabel = pct >= 65 ? 'High Confidence' : pct >= 35 ? 'Moderate' : pct > 0 ? 'Low Confidence' : null
  const tierClass = pct >= 65 ? 'strong' : pct >= 35 ? 'possible' : 'low'

  // Format duration range
  const durationText = ce?.durationLow != null && ce?.durationHigh != null
    ? ce.durationLow === ce.durationHigh
      ? `${ce.durationLow} day${ce.durationLow !== 1 ? 's' : ''}`
      : `${ce.durationLow}–${ce.durationHigh} days`
    : null

  // Format value range
  const valueText = ce && ce.lowEstimate > 0
    ? `${formatCurrency(ce.lowEstimate)} – ${formatCurrency(ce.highEstimate)}`
    : null

  // Scope summary — prefer cost estimate, fall back to aiSummary
  const scopeSummary = ce?.scopeSummary || aiSummary || null

  // Confidence statements
  const confidenceStatements = (ce?.confidenceExplanation || []) as import('@/types').ConfidenceStatement[]

  // ── Skeleton loading state ──
  if (isLoading) {
    return (
      <div className="ai-card ai-card-in">
        <div className="ai-card-glow" />
        <div className="ai-card-body">
          {/* Skeleton ring */}
          <div className="ai-card-ring-section">
            <div className="ai-card-ring-wrap">
              <svg className="ai-card-ring-svg" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                <circle
                  className="ai-card-skeleton-arc"
                  cx="60" cy="60" r="52" fill="none"
                  stroke="rgba(255,255,255,0.12)"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={`${0.25 * circumference} ${circumference}`}
                  transform="rotate(-90 60 60)"
                />
              </svg>
            </div>
          </div>

          <div className="ai-card-divider" />

          <div className="ai-card-right">
            <div className="ai-card-header">
              <span className="ai-card-title"><span className="ai-card-title-paver">Paver</span> AI Insights</span>
            </div>
            <div className="ai-card-skeleton-content">
              <div className="ai-card-skeleton-row">
                <div className="ai-card-skeleton-pulse" style={{ width: '40%', height: 12 }} />
                <div className="ai-card-skeleton-pulse" style={{ width: '30%', height: 12 }} />
              </div>
              <div className="ai-card-skeleton-row">
                <div className="ai-card-skeleton-pulse" style={{ width: '55%', height: 18 }} />
                <div className="ai-card-skeleton-pulse" style={{ width: '35%', height: 18 }} />
              </div>
              <div className="ai-card-skeleton-pulse" style={{ width: '80%', height: 12, marginTop: 8 }} />
              <div className="ai-card-analyzing">
                <span className="ai-card-analyzing-dot" />
                <span className="ai-card-analyzing-dot" />
                <span className="ai-card-analyzing-dot" />
                <span className="ai-card-analyzing-text">Analyzing opportunity</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`ai-card ${revealReady ? 'ai-card-reveal' : ''} ${animated ? 'ai-card-in' : ''}`}>
      {/* Tier badge */}
      {tierLabel && (
        <span className={`ai-card-tier ai-card-tier-${tierClass}`}>{tierLabel}</span>
      )}

      {/* Ambient glow */}
      <div className="ai-card-glow" />

      {/* Body: ring left | divider | insights right */}
      <div className="ai-card-body">
        {/* Confidence Ring */}
        {hasScore && (
          <div className="ai-card-ring-section">
            <div className="ai-card-ring-wrap">
              <div className="ai-card-ring-glow" style={{ opacity: animated ? 0.4 : 0 }} />
              <svg className="ai-card-ring-svg" viewBox="0 0 120 120">
                <defs>
                  <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#7CDBE7" />
                    <stop offset="50%" stopColor="#0074F4" />
                    <stop offset="100%" stopColor="#8B47FB" />
                  </linearGradient>
                </defs>
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                <circle
                  className="ai-card-arc"
                  cx="60" cy="60" r="52" fill="none"
                  stroke="url(#gaugeGrad)"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={`${dashLen} ${circumference}`}
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="57" className="ai-card-pct" textAnchor="middle" dominantBaseline="central">
                  {countUp}%
                </text>
                <text x="60" y="85" className="ai-card-pct-label" textAnchor="middle">
                  confidence
                </text>
              </svg>
            </div>
          </div>
        )}

        {/* Vertical divider */}
        {hasScore && (scopeSummary || valueText) && (
          <div className="ai-card-divider" />
        )}

        {/* Right side: header + key stats + scope summary */}
        <div className="ai-card-right">
          {/* Header */}
          <div className="ai-card-header">
            <span className="ai-card-title"><span className="ai-card-title-paver">Paver</span> AI Insights</span>
          </div>

          {/* Key stats row: estimated value + duration */}
          {(valueText || durationText) && (
            <div className="ai-card-stats-row">
              {valueText && (
                <div className="ai-card-stat">
                  <span className="ai-card-stat-label">Estimated Value</span>
                  <span className="ai-card-stat-value">{valueText}</span>
                </div>
              )}
              {durationText && (
                <div className="ai-card-stat">
                  <span className="ai-card-stat-label">Estimated Duration</span>
                  <span className="ai-card-stat-value">{durationText}</span>
                </div>
              )}
            </div>
          )}

          {/* Scope summary */}
          {scopeSummary && (
            <div className="ai-card-summary">
              <p className="ai-card-summary-text">{scopeSummary}</p>
            </div>
          )}

          {/* Show Analysis toggle */}
          {(confidenceStatements.length > 0 || ce || siteAnalysis) && (
            <>
              <button
                className="ai-card-breakdown-link"
                onClick={() => setShowAnalysis(!showAnalysis)}
              >
                {showAnalysis ? 'Hide Analysis' : 'Show Analysis'}
                <svg
                  width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: showAnalysis ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showAnalysis && (
                <div className="ai-card-breakdown">
                  {/* Plain-English Confidence Explanation */}
                  {confidenceStatements.length > 0 && (
                    <div className="ai-card-breakdown-section">
                      <h4 className="ai-card-breakdown-heading">
                        We scored this {pct}% because:
                      </h4>
                      <div className="ai-card-confidence-list">
                        {confidenceStatements.map((stmt, i) => (
                          <div key={i} className={`ai-card-confidence-item ai-card-confidence-${stmt.level}`}>
                            <span className="ai-card-confidence-dot" />
                            <span className="ai-card-confidence-text">{stmt.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Site Measurements (if available) */}
                  {siteAnalysis && (
                    <div className="ai-card-breakdown-section">
                      <h4 className="ai-card-breakdown-heading">Site Measurements</h4>
                      <table className="ai-card-breakdown-table">
                        <tbody>
                          {siteAnalysis.totalPavementSF != null && (
                            <tr><td>Pavement Area</td><td>{Number(siteAnalysis.totalPavementSF).toLocaleString()} SF</td></tr>
                          )}
                          {siteAnalysis.curbLF != null && Number(siteAnalysis.curbLF) > 0 && (
                            <tr><td>Curb/Gutter</td><td>{Number(siteAnalysis.curbLF).toLocaleString()} LF</td></tr>
                          )}
                          {siteAnalysis.stripingLF != null && Number(siteAnalysis.stripingLF) > 0 && (
                            <tr><td>Striping</td><td>{Number(siteAnalysis.stripingLF).toLocaleString()} LF</td></tr>
                          )}
                          {siteAnalysis.parkingStallCount != null && Number(siteAnalysis.parkingStallCount) > 0 && (
                            <tr><td>Parking Stalls</td><td>{siteAnalysis.parkingStallCount}</td></tr>
                          )}
                          {siteAnalysis.sidewalkSF != null && Number(siteAnalysis.sidewalkSF) > 0 && (
                            <tr><td>Sidewalks</td><td>{Number(siteAnalysis.sidewalkSF).toLocaleString()} SF</td></tr>
                          )}
                          {siteAnalysis.adaRampCount != null && Number(siteAnalysis.adaRampCount) > 0 && (
                            <tr><td>ADA Ramps</td><td>{siteAnalysis.adaRampCount}</td></tr>
                          )}
                        </tbody>
                      </table>
                      {siteAnalysis.conditionRating && (
                        <p className="ai-card-breakdown-note">Condition: {siteAnalysis.conditionRating}{siteAnalysis.conditionNotes ? ` — ${siteAnalysis.conditionNotes}` : ''}</p>
                      )}
                    </div>
                  )}

                  {/* Cost Breakdown */}
                  {ce && ce.total > 0 && (
                    <div className="ai-card-breakdown-section">
                      <h4 className="ai-card-breakdown-heading">Cost Breakdown</h4>
                      <table className="ai-card-breakdown-table">
                        <tbody>
                          {(ce.materialsCost ?? 0) > 0 && (
                            <tr><td>Materials</td><td>{formatCurrency(ce.materialsCost)}</td></tr>
                          )}
                          {(ce.laborCost ?? 0) > 0 && (
                            <tr><td>Labor</td><td>{formatCurrency(ce.laborCost)}</td></tr>
                          )}
                          {(ce.equipmentCost ?? 0) > 0 && (
                            <tr><td>Equipment</td><td>{formatCurrency(ce.equipmentCost)}</td></tr>
                          )}
                          {(ce.stripingCost ?? 0) > 0 && (
                            <tr><td>Striping</td><td>{formatCurrency(ce.stripingCost)}</td></tr>
                          )}
                          {(ce.miscCost ?? 0) > 0 && (
                            <tr><td>Miscellaneous</td><td>{formatCurrency(ce.miscCost)}</td></tr>
                          )}
                          <tr className="ai-card-breakdown-total">
                            <td>Estimated Total</td>
                            <td>{formatCurrency(ce.total)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Edit Opportunity Modal (for manual entries) ── */
interface EditOppModalProps {
  opp: OpportunityDetail
  onClose: () => void
  onSaved: (updated: OpportunityDetail) => void | Promise<void>
}

function EditOpportunityModal({ opp, onClose, onSaved }: EditOppModalProps) {
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: opp.title || '',
    scopeOfWork: opp.scopeOfWork || '',
    projectAddress: opp.projectAddress || '',
    contactName: opp.contactName || '',
    contactEmail: opp.contactEmail || '',
    contactPhone: opp.contactPhone || '',
    estimatedBudget: opp.estimatedBudget != null ? String(opp.estimatedBudget) : '',
    dueDate: opp.dueDate ? opp.dueDate.slice(0, 10) : '',
  })

  function handleChange(field: string, val: string) {
    setForm(prev => ({ ...prev, [field]: val }))
  }

  async function handleSave() {
    try {
      setSaving(true)
      const payload: Record<string, unknown> = {
        title: form.title,
        scopeOfWork: form.scopeOfWork || undefined,
        projectAddress: form.projectAddress || undefined,
        contactName: form.contactName || undefined,
        contactEmail: form.contactEmail || undefined,
        contactPhone: form.contactPhone || undefined,
        estimatedBudget: form.estimatedBudget ? parseFloat(form.estimatedBudget) : undefined,
        dueDate: form.dueDate || undefined,
      }
      const updated = await opportunitiesService.update(opp.id, payload)
      onSaved(updated)
      onClose()
    } catch {
      // silent
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="opp-modal-overlay">
      <div className="opp-modal-card" style={{ maxWidth: '540px' }}>
        <button className="opp-modal-close" onClick={onClose} disabled={saving}>✕</button>
        <h2 className="opp-modal-title">Edit Opportunity</h2>

        <div className="opp-tab-content">
          <div className="opp-form-group">
            <label className="opp-form-label">Title *</label>
            <input className="opp-form-input" value={form.title} onChange={e => handleChange('title', e.target.value)} disabled={saving} />
          </div>
          <div className="opp-form-group">
            <label className="opp-form-label">Scope of Work</label>
            <textarea className="opp-form-textarea" rows={6} value={form.scopeOfWork} onChange={e => handleChange('scopeOfWork', e.target.value)} disabled={saving} placeholder="Describe the work required, materials, site conditions, etc." />
          </div>
          <div className="opp-form-group">
            <label className="opp-form-label">Project Address</label>
            <input className="opp-form-input" value={form.projectAddress} onChange={e => handleChange('projectAddress', e.target.value)} disabled={saving} />
          </div>
          <div className="opp-form-row">
            <div className="opp-form-group">
              <label className="opp-form-label">Contact Name</label>
              <input className="opp-form-input" value={form.contactName} onChange={e => handleChange('contactName', e.target.value)} disabled={saving} />
            </div>
            <div className="opp-form-group">
              <label className="opp-form-label">Contact Email</label>
              <input className="opp-form-input" value={form.contactEmail} onChange={e => handleChange('contactEmail', e.target.value)} disabled={saving} />
            </div>
          </div>
          <div className="opp-form-row">
            <div className="opp-form-group">
              <label className="opp-form-label">Contact Phone</label>
              <input className="opp-form-input" value={form.contactPhone} onChange={e => handleChange('contactPhone', e.target.value)} disabled={saving} />
            </div>
            <div className="opp-form-group">
              <label className="opp-form-label">Estimated Budget</label>
              <input className="opp-form-input" type="number" value={form.estimatedBudget} onChange={e => handleChange('estimatedBudget', e.target.value)} disabled={saving} />
            </div>
          </div>
          <div className="opp-form-group">
            <label className="opp-form-label">Due Date</label>
            <input className="opp-form-input" type="date" value={form.dueDate} onChange={e => handleChange('dueDate', e.target.value)} disabled={saving} />
          </div>

          <div className="opp-modal-actions">
            <button className="opp-modal-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="opp-modal-btn-primary" onClick={handleSave} disabled={!form.title.trim() || saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════ */

export function OpportunityDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const currentUser = useAuthStore((s) => s.user)
  const userRole = currentUser?.role || ''
  const canAnalyze = userRole === 'SUPERADMIN' || userRole === 'ADMIN' || userRole === 'MANAGER'

  const notifications = useNotificationStore((s) => s.notifications)
  const optimisticMarkAsRead = useNotificationStore((s) => s.optimisticMarkAsRead)

  // Clear the sidebar badge when the estimator opens an assigned opportunity
  useEffect(() => {
    if (!id) return
    const unread = notifications.filter(
      (n) => !n.isRead && n.entityId === id
    )
    unread.forEach((n) => {
      optimisticMarkAsRead(n.id)
      notificationsService.markAsRead(n.id).catch(() => {})
    })
  }, [id, notifications, optimisticMarkAsRead])

  const [opp, setOpp] = useState<OpportunityDetail | null>(null)
  const [notes, setNotes] = useState<OpportunityNote[]>([])
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const autoAnalysisTriggered = useRef(false)
  const [_rejecting, _setRejecting] = useState(false)
  const [_rejectionReason, _setRejectionReason] = useState('')
  const [showAcceptModal, setShowAcceptModal] = useState(false)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)

  /* Assign & Brief panel */
  const [showAssignPanel, setShowAssignPanel] = useState(false)
  const [assignUsers, setAssignUsers] = useState<User[]>([])
  const [assignTo, setAssignTo] = useState('')
  const [brief, setBrief] = useState('')
  const [assigning, setAssigning] = useState(false)

  /* Start Estimate */
  const [startingEstimate, setStartingEstimate] = useState(false)

  /* Inline-editable Lead and Notes */
  const [lead, setLead] = useState(opp?.lead ?? '')
  const [estimatorNotes, setEstimatorNotes] = useState(opp?.estimatorNotes ?? '')

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([opportunitiesService.get(id), opportunitiesService.listNotes(id)])
      .then(([oppData, notesData]) => {
        setOpp(oppData)
        setNotes([...notesData].reverse())
        setLead(oppData.lead ?? '')
        setEstimatorNotes(oppData.estimatorNotes ?? '')
        // Auto-mark as reviewed if status is 'new'
        if (oppData.status === 'new') {
          opportunitiesService.markReviewed(id).catch(() => {})
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [id])

  // Auto-trigger analysis when the opportunity loads without existing analysis
  useEffect(() => {
    if (!opp || !canAnalyze || autoAnalysisTriggered.current) return
    const needsAnalysis =
      !opp.costEstimate && !opp.aiSummary &&
      opp.status !== 'converted' && opp.status !== 'dismissed' &&
      opp.status !== 'pending_analysis' && opp.status !== 'analyzing'
    if (needsAnalysis) {
      autoAnalysisTriggered.current = true
      setAnalyzing(true)
      opportunitiesService.analyze(opp.id)
        .then((updated) => setOpp(updated))
        .catch(() => {})
        .finally(() => setAnalyzing(false))
    }
  }, [opp?.id, opp?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for analysis completion when status is pending or cost estimate is missing
  useEffect(() => {
    if (!id || !opp) return
    const needsPoll = opp.status === 'pending_analysis' || opp.status === 'analyzing' || (!opp.costEstimate && opp.status === 'analyzed')
    if (!needsPoll) return
    const interval = setInterval(() => {
      opportunitiesService.get(id).then((updated) => {
        const done = updated.status !== 'pending_analysis' && updated.status !== 'analyzing' && updated.costEstimate
        if (done) {
          setOpp(updated)
          clearInterval(interval)
        }
      }).catch(() => {})
    }, 3000)
    return () => clearInterval(interval)
  }, [id, opp?.status, opp?.costEstimate])

  async function handleAddNote() {
    if (!newNote.trim() || !opp) return
    try {
      setAddingNote(true)
      const note = await opportunitiesService.addNote(opp.id, newNote.trim())
      setNotes((prev) => [...prev, note])
      setNewNote('')
    } catch {
      // silent
    } finally {
      setAddingNote(false)
    }
  }

  // async function _handleRunAnalysis() {
  //   if (!opp) return
  //   try {
  //     setAnalyzing(true)
  //     const updated = await opportunitiesService.analyze(opp.id)
  //     setOpp(updated)
  //   } catch {
  //     // silent
  //   } finally {
  //     setAnalyzing(false)
  //   }
  // }

  async function handleOpenAssignPanel() {
    setShowAssignPanel(true)
    if (assignUsers.length === 0) {
      usersService.list().then(setAssignUsers).catch(() => {})
    }
  }

  async function handleAssign() {
    if (!opp || !assignTo) return
    try {
      setAssigning(true)
      const updated = await opportunitiesService.assign(opp.id, assignTo, brief.trim() || undefined)
      setOpp(updated as OpportunityDetail)
      // Reload notes to show the brief as the first note
      const updatedNotes = await opportunitiesService.listNotes(opp.id)
      setNotes([...updatedNotes].reverse())
      setShowAssignPanel(false)
      setBrief('')
      setAssignTo('')
    } catch {
      // silent
    } finally {
      setAssigning(false)
    }
  }

  const [actionError, setActionError] = useState('')

  async function handleStartEstimate() {
    if (!opp) return
    try {
      setStartingEstimate(true)
      setActionError('')
      const result = await opportunitiesService.startEstimate(opp.id)
      navigate(`/estimates/${result.estimate.id}`)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to start estimate'
      setActionError(msg)
      console.error('[start-estimate]', msg)
    } finally {
      setStartingEstimate(false)
    }
  }

  // async function _handleDismiss() {
  //   if (!opp || !rejectionReason.trim()) return
  //   try {
  //     _setRejecting(true)
  //     const updated = await opportunitiesService.dismiss(opp.id)
  //     setOpp(updated as OpportunityDetail)
  //     setRejectionReason('')
  //   } catch {
  //     // silent
  //   } finally {
  //     _setRejecting(false)
  //   }
  // }

  if (loading) {
    return (
      <PageLoader />
    )
  }

  if (!opp) {
    return (
      <div className="rfp-loading">
        <p>Opportunity not found.</p>
      </div>
    )
  }

  const isEditable = opp.source !== 'sam_gov'

  // Confidence score — prefer cost estimate confidence, fall back to opportunity-level
  const overallConfidence = opp.costEstimate?.overallConfidence
    ?? opp.confidenceScore
    ?? opp.siteAnalysis?.overallConfidence
    ?? null

  return (
    <div className="opp-detail">
      {/* ── Page Header (matches estimate builder) ── */}
      <div className="est-page-head">
        <div className="est-title-group">
          <h1 className="est-title">
            {opp.title}
            {isEditable && (
              <button className="est-edit-btn" onClick={() => setEditModalOpen(true)} title="Edit opportunity details">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
              </button>
            )}
          </h1>
        </div>
        <div className="opp-head-actions">
          {/* Start Estimate — visible if unassigned OR assigned to current user; once assigned to someone else, must reassign to yourself first */}
          {opp.status !== 'converted' && opp.status !== 'dismissed' && (!opp.assignedToUserId || opp.assignedToUserId === currentUser?.id) && (
            <button
              className="opp-btn-start-estimate"
              onClick={handleStartEstimate}
              disabled={startingEstimate}
            >
              {startingEstimate ? 'Starting...' : 'Start Estimate'}
            </button>
          )}
{/* Assign & Brief — manager/admin only, not already converted */}
          {canAnalyze && opp.status !== 'converted' && opp.status !== 'dismissed' && (
            <button
              className="opp-btn-assign"
              onClick={handleOpenAssignPanel}
            >
              {opp.assignedToUserId ? 'Reassign' : 'Assign & Brief'}
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <div className="opp-action-error">{actionError}</div>
      )}

      {/* ── ID + indicator badges ── */}
      <div className="opp-head-meta">
        {opp.opportunityNumber && (
          <span className="rfp-view-id-badge">{opp.opportunityNumber}</span>
        )}
        {opp.prevailingWage && (
          <span className="opp-indicator-badge opp-indicator-pw">Prevailing Wage</span>
        )}
        {opp.bondingRequired && (
          <span className="opp-indicator-badge opp-indicator-bond">Bonding Required</span>
        )}
      </div>

      {/* AI Insights Card — always visible; shows shimmer until analysis completes */}
      <AiInsightsCard
        overallConfidence={overallConfidence}
        costEstimate={opp.costEstimate}
        siteAnalysis={opp.siteAnalysis}
        aiSummary={opp.aiSummary}
        pendingAnalysis={opp.status === 'pending_analysis' || opp.status === 'analyzing' || analyzing}
      />

      {/* ── Main Grid ── */}
      <div className="opp-detail-grid">
        {/* ── Left Column ── */}
        <div className="opp-detail-main">
          {/* Satellite Map (if address available) */}
          {opp.projectAddress && (
            <SatelliteMapCard address={opp.projectAddress} lat={opp.latitude} lng={opp.longitude} />
          )}

          {/* Project Details — inline-editable Lead and Notes */}
          <div className="opp-section">
            <h3 className="opp-section-title">Project Details</h3>
            <div className="opp-form-group">
              <label className="opp-form-label">Lead</label>
              <input
                type="text"
                maxLength={80}
                className="opp-form-input"
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                onBlur={() => opportunitiesService.update(opp.id, { lead: lead.trim() })}
                placeholder="e.g., PC, Kym, Cold call"
              />
            </div>
            <div className="opp-form-group">
              <label className="opp-form-label">Notes</label>
              <textarea
                rows={3}
                className="opp-form-textarea"
                value={estimatorNotes}
                onChange={(e) => setEstimatorNotes(e.target.value)}
                onBlur={() => opportunitiesService.update(opp.id, { estimatorNotes: estimatorNotes.trim() })}
                placeholder="e.g., walked site 5/13, waiting on plans"
              />
            </div>
          </div>

          {/* Contact Info — hidden for SAM.gov jobs since it appears in the sidebar panel */}
          {opp.source !== 'sam_gov' && (opp.contactName || opp.contactEmail || opp.contactPhone) && (
            <div className="opp-section">
              <h3 className="opp-section-title">Contact</h3>
              <div className="opp-contact-row">
                {opp.contactName && <span className="opp-contact-name">{opp.contactName}</span>}
                {opp.contactPhone && (
                  <a href={`tel:${opp.contactPhone}`} className="opp-contact-link">{opp.contactPhone}</a>
                )}
                {opp.contactEmail && (
                  <a href={`mailto:${opp.contactEmail}`} className="opp-contact-link">{opp.contactEmail}</a>
                )}
              </div>
            </div>
          )}

          {/* Scope of Work */}
          {opp.scopeOfWork && (
            <div className="opp-section">
              <h3 className="opp-section-title">Scope of Work</h3>
              <ScopeOfWorkRenderer text={opp.scopeOfWork} />
            </div>
          )}

          {/* Documents */}
          {(() => {
            const docs: { url: string; name: string; type: string }[] = []
            // Source PDF
            if (opp.sourcePdfUrl) {
              docs.push({ url: opp.sourcePdfUrl, name: opp.sourceFileName || 'Source Document.pdf', type: 'pdf' })
            }
            // SAM.gov attachment URLs require an API key header and cannot be
            // used as direct browser links — omit them here. Users can view all
            // documents via the "View on SAM.gov" button in the header.
            if (docs.length === 0) return null
            return (
              <div className="opp-section">
                <h3 className="opp-section-title">Documents</h3>
                <div className="opp-docs-list">
                  {docs.map((doc, i) => (
                    <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" className="opp-doc-card">
                      <div className="opp-doc-icon">
                        <FileIcon />
                      </div>
                      <div className="opp-doc-info">
                        <span className="opp-doc-name">{doc.name}</span>
                        <span className="opp-doc-type">{doc.type.toUpperCase()}</span>
                      </div>
                      <div className="opp-doc-download">
                        <DownloadIcon />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )
          })()}

          {/* Site Analysis & Cost Breakdown moved into AI Insights card breakdown */}
        </div>

        {/* ── Right Column (Sidebar) ── */}
        <div className="opp-detail-sidebar">
          {/* SAM.gov Solicitation Panel */}
          {opp.source === 'sam_gov' && (
            <div className="opp-sam-panel">
              <h3>SAM.gov Solicitation</h3>
              <div className="opp-sam-grid">
                {opp.samSolicitationNum && (
                  <div className="opp-sam-field">
                    <div className="opp-sam-label">Solicitation #</div>
                    <div className="opp-sam-value">{opp.samSolicitationNum}</div>
                  </div>
                )}
                {opp.samNaicsCode && (
                  <div className="opp-sam-field">
                    <div className="opp-sam-label">NAICS Code</div>
                    <div className="opp-sam-value">
                      {opp.samNaicsCode}
                      {naicsLabel(opp.samNaicsCode) && (
                        <div className="opp-sam-naics-desc">{naicsLabel(opp.samNaicsCode)}</div>
                      )}
                    </div>
                  </div>
                )}
                {(opp.samSetAsideDesc || opp.samSetAside) && (
                  <div className="opp-sam-field">
                    <div className="opp-sam-label">Set-Aside</div>
                    <div className="opp-sam-value">{opp.samSetAsideDesc || opp.samSetAside}</div>
                  </div>
                )}
                {opp.samDepartment && (
                  <div className="opp-sam-field">
                    <div className="opp-sam-label">Department</div>
                    <div className="opp-sam-value">{opp.samDepartment}</div>
                  </div>
                )}
                {opp.samDeadline && (
                  <div className="opp-sam-field">
                    <div className="opp-sam-label">Response Deadline</div>
                    <div className="opp-sam-value">
                      {new Date(opp.samDeadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {(() => {
                        const daysLeft = Math.floor((new Date(opp.samDeadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                        if (daysLeft < 7) {
                          return <div className="opp-deadline-urgent">{daysLeft} days remaining</div>
                        }
                        return null
                      })()}
                    </div>
                  </div>
                )}
                {(opp.samContactName || opp.samContactEmail) && (
                  <div className="opp-sam-field">
                    <div className="opp-sam-label">SAM.gov Contact</div>
                    <div className="opp-sam-value">
                      {opp.samContactName && <div>{opp.samContactName}</div>}
                      {opp.samContactEmail && <a href={`mailto:${opp.samContactEmail}`} className="opp-sam-link">{opp.samContactEmail}</a>}
                      {opp.samContactPhone && <div>{opp.samContactPhone}</div>}
                    </div>
                  </div>
                )}
              </div>
              {opp.samLink && (
                <div className="opp-sam-link-row">
                  <a href={opp.samLink} target="_blank" rel="noopener noreferrer" className="rfp-btn-secondary opp-sam-view-btn">
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    View on SAM.gov
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Source Document */}
          {(opp.sourcePdfUrl || opp.sourceUrl) && (
            <div className="opp-section">
              <h3 className="opp-section-title">Source Document</h3>
              {opp.sourcePdfUrl && (
                <a href={opp.sourcePdfUrl} target="_blank" rel="noopener noreferrer" className="rfp-view-doc-card">
                  <div className="rfp-view-doc-icon"><FileIcon /></div>
                  <div className="rfp-view-doc-info">
                    <span className="rfp-view-doc-name">{opp.sourceFileName || 'Document.pdf'}</span>
                    <span className="rfp-view-doc-action"><DownloadIcon /> View PDF</span>
                  </div>
                </a>
              )}
              {opp.sourceUrl && (
                <a href={opp.sourceUrl} target="_blank" rel="noopener noreferrer" className="opp-source-link-card">
                  <ExternalLinkIcon />
                  <span>{opp.sourceUrl}</span>
                </a>
              )}
            </div>
          )}

          {/* Assigned To */}
          {opp.assignedToUser && (
            <div className="opp-section">
              <h3 className="opp-section-title">Assigned To</h3>
              <div className="rfp-assign-preview" style={{ marginTop: 0 }}>
                {(opp.assignedToUser as any).avatarUrl ? (
                  <img src={(opp.assignedToUser as any).avatarUrl} alt="" className="rfp-assign-avatar" />
                ) : (
                  <div className="rfp-assign-avatar-placeholder">
                    {opp.assignedToUser.displayName?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <p className="rfp-assign-name">{opp.assignedToUser.displayName}</p>
                  <p className="rfp-assign-role">{(opp.assignedToUser as any).role || 'Estimator'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Notes Thread */}
          <div className="opp-section">
            <h3 className="opp-section-title">Notes</h3>
            {notes.length > 0 && (
              <div className="rfp-chat-thread" style={{ marginBottom: 12 }}>
                {notes.map((note) => {
                  const isMine = note.author?.id === currentUser?.id
                  return (
                    <div key={note.id} className={`rfp-chat-row ${isMine ? 'rfp-chat-row-right' : 'rfp-chat-row-left'}`}>
                      {!isMine && (
                        <div className="rfp-chat-avatar">
                          {note.author?.avatarUrl ? (
                            <img src={note.author.avatarUrl} alt="" className="rfp-chat-avatar-img" />
                          ) : (
                            <div className="rfp-assign-avatar-placeholder" style={{ width: 28, height: 28, fontSize: 11 }}>
                              {note.author?.displayName?.charAt(0) || '?'}
                            </div>
                          )}
                        </div>
                      )}
                      <div className={`rfp-chat-bubble ${isMine ? 'rfp-chat-bubble-mine' : 'rfp-chat-bubble-theirs'}`}>
                        <div className="rfp-chat-bubble-header">
                          <span className="rfp-chat-bubble-author">{note.author?.displayName || 'Unknown'}</span>
                        </div>
                        <p className="rfp-chat-bubble-text">{note.content}</p>
                      </div>
                      {isMine && (
                        <div className="rfp-chat-avatar">
                          {currentUser?.avatarUrl ? (
                            <img src={currentUser.avatarUrl} alt="" className="rfp-chat-avatar-img" />
                          ) : (
                            <div className="rfp-assign-avatar-placeholder" style={{ width: 28, height: 28, fontSize: 11 }}>
                              {currentUser?.displayName?.charAt(0) || '?'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            <textarea
              className="rfp-create-textarea"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note..."
              rows={3}
            />
            {newNote.trim() && (
              <button
                className="rfp-btn-primary"
                onClick={handleAddNote}
                disabled={addingNote}
                style={{ alignSelf: 'flex-end', padding: '8px 20px', fontSize: 13, marginTop: 8 }}
              >
                {addingNote ? 'Sending...' : 'Send'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Assign & Brief panel */}
      {showAssignPanel && (
        <div className="opp-modal-overlay">
          <div className="opp-modal-card" style={{ maxWidth: 480 }}>
            <button className="opp-modal-close" onClick={() => setShowAssignPanel(false)} disabled={assigning}>✕</button>
            <h2 className="opp-modal-title">Assign & Brief</h2>
            <div className="opp-tab-content">
              <div className="opp-form-group">
                <label className="opp-form-label">Assign To <span className="rfp-required">*</span></label>
                <select className="rfp-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} disabled={assigning}>
                  <option value="">— Select Estimator —</option>
                  {assignUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.displayName}</option>
                  ))}
                </select>
              </div>
              <div className="opp-form-group">
                <label className="opp-form-label">Handoff Brief</label>
                <p className="opp-create-assign-hint">This will appear as the first note so the estimator sees it when they open this opportunity.</p>
                <textarea
                  className="opp-form-textarea"
                  rows={5}
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="Add context, priorities, or anything the estimator should know..."
                  disabled={assigning}
                />
              </div>
              <div className="opp-modal-actions">
                <button className="opp-modal-btn-secondary" onClick={() => setShowAssignPanel(false)} disabled={assigning}>Cancel</button>
                <button className="opp-modal-btn-primary" onClick={handleAssign} disabled={!assignTo || assigning}>
                  {assigning ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAcceptModal && (
        <AcceptOpportunityModal
          opportunityId={opp.id}
          opportunityTitle={opp.title}
          onClose={() => setShowAcceptModal(false)}
          onAccepted={(updated: any) => {
            if (updated) setOpp(updated)
            setShowAcceptModal(false)
          }}
        />
      )}

      {showConvertModal && (
        <ConvertToRfpModal
          opportunityId={opp.id}
          opportunityTitle={opp.title}
          opportunityAddress={opp.projectAddress}
          opportunityProjectTypes={opp.projectTypes}
          opportunityScope={opp.scopeOfWork || opp.aiSummary}
          opportunityContact={opp.contactName}
          opportunityContactEmail={opp.contactEmail}
          onClose={() => setShowConvertModal(false)}
          onConverted={(rfpId: string) => {
            setShowConvertModal(false)
            navigate(`/rfp-requests/${rfpId}`)
          }}
        />
      )}

      {editModalOpen && (
        <EditOpportunityModal
          opp={opp}
          onClose={() => setEditModalOpen(false)}
          onSaved={async (updated) => {
            setEditModalOpen(false)
            // Clear existing AI results so the shimmer/skeleton shows during re-analysis
            setOpp({ ...(updated as OpportunityDetail), costEstimate: undefined, siteAnalysis: undefined, aiSummary: undefined })
            // Re-run AI analysis so insights reflect the updated scope of work
            try {
              setAnalyzing(true)
              const analyzed = await opportunitiesService.analyze(updated.id)
              setOpp(analyzed as OpportunityDetail)
            } catch {
              // silent — restore previous results so page isn't blank
              setOpp(updated as OpportunityDetail)
            } finally {
              setAnalyzing(false)
            }
          }}
        />
      )}
    </div>
  )
}
